// Pure helpers for how the Models tab groups and highlights candidates.

import type {
  CandidateStatus,
  ModelCandidate,
  ModelCurrencyOverview,
} from "@/services/adminModelCurrency";
import { canPromote } from "./helpers";
import { isOutOfDate } from "./compareHelpers";

/** Decided candidates: tucked under "Older results", not shown as open work. */
const HISTORY: CandidateStatus[] = ["promoted", "dismissed", "superseded"];

export function splitCandidates(
  candidates: ModelCandidate[],
  currentModel: string
): {
  open: ModelCandidate[];
  history: ModelCandidate[];
} {
  const older = (c: ModelCandidate) => HISTORY.includes(c.status) || isOutOfDate(c, currentModel);
  return {
    open: candidates.filter(c => !older(c)),
    history: candidates.filter(older),
  };
}

/** Passed against the model in use today. */
function readyNow(candidate: ModelCandidate, currentModel: string): boolean {
  return canPromote(candidate) && !isOutOfDate(candidate, currentModel);
}

export interface AttentionCounts {
  ready: number;
  waiting: number;
  failed: number;
}

export function attentionCounts(overview: ModelCurrencyOverview): AttentionCounts {
  const counts: AttentionCounts = { ready: 0, waiting: 0, failed: 0 };
  for (const useCase of overview.useCases) {
    const { open } = splitCandidates(useCase.candidates, useCase.effective.model);
    for (const c of open) {
      if (readyNow(c, useCase.effective.model)) counts.ready += 1;
      else if (c.status === "detected" || c.status === "evaluating") counts.waiting += 1;
      else if (c.status === "failed") counts.failed += 1;
    }
  }
  return counts;
}

export function attentionSummary({ ready, waiting, failed }: AttentionCounts): string {
  const parts: string[] = [];
  if (ready) parts.push(`${ready} ${ready === 1 ? "model" : "models"} ready to promote`);
  if (waiting) parts.push(`${waiting} waiting for a test`);
  if (failed) parts.push(`${failed} failed or couldn't be tested`);
  return parts.length ? parts.join(" · ") : "Every feature is on its newest tested model.";
}

export type CandidateTone = "ready" | "problem" | "pending" | "closed";

export function candidateTone(candidate: ModelCandidate, currentModel: string): CandidateTone {
  if (isOutOfDate(candidate, currentModel)) return "closed";
  if (candidate.status === "passed") return "ready";
  if (candidate.status === "failed") return "problem";
  if (candidate.status === "detected" || candidate.status === "evaluating") return "pending";
  return "closed";
}

/** Why Promote is greyed out, so a disabled button never stands there unexplained. */
export function promoteBlockedReason(
  candidate: ModelCandidate,
  currentModel: string
): string | null {
  if (isOutOfDate(candidate, currentModel)) {
    const why = `Tested against ${candidate.evalResults?.incumbentModel}, not ${currentModel}, so this result can't be used to promote.`;
    return candidate.canRunHere ? `${why} Test it again to compare with ${currentModel}.` : why;
  }
  if (canPromote(candidate)) return null;
  if (candidate.status === "failed") return "Didn't pass, so it can't be promoted.";
  if (candidate.status === "passed") return "The test result is incomplete. Run it again.";
  return "Promote unlocks once the test passes.";
}

/** A pass measured against a model no longer in use can be tested again from this page. */
export function canRetest(
  candidate: ModelCandidate,
  writable: boolean,
  currentModel: string
): boolean {
  return (
    writable &&
    candidate.canRunHere &&
    candidate.status === "passed" &&
    isOutOfDate(candidate, currentModel)
  );
}
